import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Badge, Form, Row, Col } from "react-bootstrap";

import { ResidenteService } from "services/ResidenteService";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import {
  notifySuccess,
  notifyError,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import DatePickerSingle from "jsx/components/CitoPlus/components/DatePickerSingle";

const DIAS_SEMANA = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const ReservaModal = ({ show, onHide, zona, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [descripcion, setDescripcion] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateForHour, setSelectedDateForHour] = useState(null);
  const [selectedHours, setSelectedHours] = useState([]);
  const [reservasMesCount, setReservasMesCount] = useState(0);
  const [loadingReservas, setLoadingReservas] = useState(false);

  useEffect(() => {
    if (show) {
      setDescripcion("");
      setSelectedDate(null);
      setSelectedDateForHour(null);
      setSelectedHours([]);
    }
  }, [show]);

  const fetchReservasMes = useCallback(async () => {
    if (!zona || zona.max_reservas_por_vivienda_mes === 0) return;
    setLoadingReservas(true);
    try {
      const hoy = new Date();
      const response = await ResidenteService.getReservas();
      const reservasZona = (response.data.results || []).filter((r) => {
        const fecha = new Date(r.start);
        return (
          r.zona_uuid === zona.uuid &&
          fecha.getMonth() === hoy.getMonth() &&
          fecha.getFullYear() === hoy.getFullYear() &&
          (r.estado === "pendiente" || r.estado === "confirmada")
        );
      });
      setReservasMesCount(reservasZona.length);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingReservas(false);
    }
  }, [zona]);

  useEffect(() => {
    if (show && zona && zona.max_reservas_por_vivienda_mes > 0) {
      fetchReservasMes();
    }
  }, [show, zona, fetchReservasMes]);

  const isHourPast = useCallback(
    (hour) => {
      if (!selectedDateForHour) return false;

      const today = new Date();
      const todayStr = today.toISOString().split("T")[0];
      const selectedStr = selectedDateForHour.toISOString().split("T")[0];

      if (todayStr !== selectedStr) return false;

      const currentHour = today.getHours();
      return hour <= currentHour;
    },
    [selectedDateForHour],
  );

  const availableHours = useMemo(() => {
    if (!zona || zona.tipo_reserva !== "hora" || !selectedDateForHour)
      return [];

    const hours = [];
    const [horaInicio] = zona.horario_inicio.split(":");
    const [horaFin] = zona.horario_fin.split(":");
    const startHour = parseInt(horaInicio);
    const endHour = parseInt(horaFin);

    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    const selectedStr = selectedDateForHour.toISOString().split("T")[0];
    const isToday = todayStr === selectedStr;
    const currentHour = today.getHours();

    for (let h = startHour; h < endHour; h++) {
      if (!isToday || h > currentHour) {
        hours.push(h);
      }
    }
    return hours;
  }, [zona, selectedDateForHour]);

  const formatHour = (hour) => {
    return `${hour.toString().padStart(2, "0")}:00`;
  };

  const handleHourClick = (hour) => {
    if (isHourPast(hour)) {
      notifyError("No puedes seleccionar horas que ya pasaron");
      return;
    }

    if (selectedHours.length === 0) {
      setSelectedHours([hour]);
      return;
    }

    const minSelected = Math.min(...selectedHours);
    const maxSelected = Math.max(...selectedHours);

    if (hour === minSelected - 1 || hour === maxSelected + 1) {
      if (selectedHours.length < zona.tiempo_maximo_reserva) {
        setSelectedHours([...selectedHours, hour].sort((a, b) => a - b));
      } else {
        notifyError(`Máximo ${zona.tiempo_maximo_reserva} horas permitidas`);
      }
    } else if (selectedHours.includes(hour)) {
      const newHours = selectedHours.filter((h) => h !== hour);
      if (newHours.length > 0) {
        const newMin = Math.min(...newHours);
        const newMax = Math.max(...newHours);
        let isConsecutive = true;
        for (let h = newMin; h <= newMax; h++) {
          if (!newHours.includes(h)) {
            isConsecutive = false;
            break;
          }
        }
        if (isConsecutive) {
          setSelectedHours(newHours);
        } else {
          notifyError("Las horas deben ser consecutivas");
          setSelectedHours(newHours);
        }
      } else {
        setSelectedHours([]);
      }
    } else {
      setSelectedHours([hour]);
    }
  };

  const isHourSelected = (hour) => selectedHours.includes(hour);

  const getSelectedHoursText = () => {
    if (selectedHours.length === 0) return "";
    const min = Math.min(...selectedHours);
    const max = Math.max(...selectedHours);
    return `${formatHour(min)} - ${formatHour(max + 1)} (${selectedHours.length} hora${selectedHours.length > 1 ? "s" : ""})`;
  };

  const isDateAvailable = useCallback(
    (date) => {
      if (!zona || !zona.dias_operacion) return true;
      const diaSemana = date.getDay() === 0 ? 6 : date.getDay() - 1;
      return zona.dias_operacion.includes(diaSemana);
    },
    [zona],
  );

  const isDatePast = useCallback((date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }, []);

  const handleDateChangeForHour = useCallback(
    (date) => {
      if (!date) return;

      if (isDatePast(date)) {
        notifyError("No puedes seleccionar una fecha pasada");
        return;
      }

      if (!isDateAvailable(date)) {
        notifyError("Este día no está disponible para reservas");
        return;
      }

      setSelectedDateForHour(date);
      setSelectedHours([]);
    },
    [isDatePast, isDateAvailable],
  );

  const handleDateChangeForDay = useCallback(
    (date) => {
      if (!date) return;

      if (isDatePast(date)) {
        notifyError("No puedes seleccionar una fecha pasada");
        return;
      }

      if (!isDateAvailable(date)) {
        notifyError("Este día no está disponible para reservas");
        return;
      }

      setSelectedDate(date);
    },
    [isDatePast, isDateAvailable],
  );

  const formatDiasOperacion = (dias) => {
    if (!dias || dias.length === 0) return "No disponible";
    if (dias.length === 7) return "Todos los días";
    return dias.map((d) => DIAS_SEMANA[d]).join(", ");
  };

  const limiteAlcanzado =
    zona &&
    zona.max_reservas_por_vivienda_mes > 0 &&
    reservasMesCount >= zona.max_reservas_por_vivienda_mes;

  const handleSubmit = async () => {
    if (!zona) return;

    if (limiteAlcanzado) {
      notifyError(
        `Has alcanzado el límite de ${zona.max_reservas_por_vivienda_mes} reservas mensuales para esta zona`,
      );
      return;
    }

    if (zona.tipo_reserva === "hora") {
      if (!selectedDateForHour) {
        notifyError("Selecciona una fecha");
        return;
      }
      if (selectedHours.length === 0) {
        notifyError("Selecciona al menos una hora");
        return;
      }
    }

    if (zona.tipo_reserva === "dia" && !selectedDate) {
      notifyError("Selecciona una fecha");
      return;
    }

    setLoading(true);
    try {
      let fechaInicio, fechaFin;

      if (zona.tipo_reserva === "hora") {
        const minHour = Math.min(...selectedHours);
        const maxHour = Math.max(...selectedHours);
        const dateStr = selectedDateForHour.toISOString().split("T")[0];
        fechaInicio = new Date(`${dateStr}T${formatHour(minHour)}`);
        fechaFin = new Date(`${dateStr}T${formatHour(maxHour + 1)}`);
      } else {
        const [horaInicio] = zona.horario_inicio.split(":");
        const [horaFin] = zona.horario_fin.split(":");
        fechaInicio = new Date(selectedDate);
        fechaInicio.setHours(parseInt(horaInicio), 0, 0, 0);
        fechaFin = new Date(selectedDate);
        fechaFin.setHours(parseInt(horaFin), 0, 0, 0);
      }

      const dataToSend = {
        zona_uuid: zona.uuid,
        titulo: `Reserva ${zona.nombre}`,
        descripcion: descripcion || "",
        fecha_inicio: fechaInicio.toISOString(),
        fecha_fin: fechaFin.toISOString(),
        all_day: zona.tipo_reserva === "dia",
      };

      await ResidenteService.createReserva(dataToSend);
      notifySuccess("Reserva creada correctamente");
      onSuccess && onSuccess();
      onHide();
    } catch (error) {
      notifyError("Error al crear la reserva");
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  if (!zona) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center">
          <span>{zona.nombre}</span>
          <Badge
            bg={zona.tipo_reserva === "hora" ? "info" : "primary"}
            className="ms-2"
          >
            {zona.tipo_reserva === "hora"
              ? `Por hora (${zona.tiempo_maximo_reserva}h máx)`
              : "Por día completo"}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={7}>
            <div className="mb-3 p-3 bg-light rounded">
              <Row className="g-2">
                <Col xs={6}>
                  <small className="text-muted d-block">Horario</small>
                  <strong>
                    <i className="fas fa-clock me-1 text-primary"></i>
                    {zona.horario_inicio} - {zona.horario_fin}
                  </strong>
                </Col>
                <Col xs={6}>
                  <small className="text-muted d-block">Días disponibles</small>
                  <strong>
                    <i className="fas fa-calendar me-1 text-primary"></i>
                    {zona.dias_operacion_display
                      ? zona.dias_operacion_display.join(", ")
                      : formatDiasOperacion(zona.dias_operacion)}
                  </strong>
                </Col>
                <Col xs={6}>
                  <small className="text-muted d-block">Capacidad</small>
                  <strong>
                    <i className="fas fa-users me-1 text-primary"></i>
                    {zona.capacidad_min} - {zona.capacidad_max} personas
                  </strong>
                </Col>
                {zona.max_reservas_por_vivienda_mes > 0 && (
                  <Col xs={6}>
                    <small className="text-muted d-block">
                      Reservas este mes
                    </small>
                    {loadingReservas ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      <strong className={limiteAlcanzado ? "text-danger" : ""}>
                        <i className="fas fa-calendar-check me-1 text-primary"></i>
                        {reservasMesCount} /{" "}
                        {zona.max_reservas_por_vivienda_mes}
                      </strong>
                    )}
                  </Col>
                )}
                {zona.requiere_aprobacion_manual && (
                  <Col xs={6}>
                    <small className="text-muted d-block">Aprobación</small>
                    <strong className="text-warning">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      Requiere aprobación
                    </strong>
                  </Col>
                )}
              </Row>
            </div>

            {zona.tipo_reserva === "hora" && (
              <>
                <div className="mb-3">
                  <Form.Label className="fw-bold">
                    <i className="fas fa-calendar-alt me-2"></i>
                    Selecciona la fecha
                  </Form.Label>
                  <br />
                  <DatePickerSingle
                    selectedDate={selectedDateForHour}
                    onChange={handleDateChangeForHour}
                    placeholderText="Selecciona una fecha"
                    filterDate={isDateAvailable}
                    minDate={new Date()}
                  />
                  {selectedDateForHour && (
                    <div className="mt-2">
                      <Badge bg="success" className="py-2 px-3">
                        <i className="fas fa-check-circle me-1"></i>
                        {selectedDateForHour.toLocaleDateString("es-CO", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </Badge>
                    </div>
                  )}
                </div>

                {selectedDateForHour && (
                  <div className="mb-3">
                    <Form.Label className="fw-bold">
                      <i className="fas fa-clock me-2"></i>
                      Selecciona las horas
                    </Form.Label>
                    <small className="text-muted d-block mb-2">
                      Click para seleccionar horas consecutivas (máx.{" "}
                      {zona.tiempo_maximo_reserva}h)
                    </small>
                    {availableHours.length === 0 ? (
                      <div className="alert alert-warning py-2">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        No hay horas disponibles. El horario de hoy ya pasó.
                      </div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {availableHours.map((hour) => (
                          <button
                            key={hour}
                            type="button"
                            className={`btn ${isHourSelected(hour) ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => handleHourClick(hour)}
                            style={{ minWidth: "70px" }}
                          >
                            {formatHour(hour)}
                          </button>
                        ))}
                      </div>
                    )}
                    {selectedHours.length > 0 && (
                      <div className="mt-2">
                        <Badge bg="success" className="py-2 px-3">
                          <i className="fas fa-check-circle me-1"></i>
                          {getSelectedHoursText()}
                        </Badge>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {zona.tipo_reserva === "dia" && (
              <div className="mb-3">
                <Form.Label className="fw-bold">
                  <i className="fas fa-calendar-alt me-2"></i>
                  Selecciona la fecha
                </Form.Label>
                <br />
                <DatePickerSingle
                  selectedDate={selectedDate}
                  onChange={handleDateChangeForDay}
                  placeholderText="Selecciona una fecha"
                  filterDate={isDateAvailable}
                  minDate={new Date()}
                />
                {selectedDate && (
                  <div className="mt-2">
                    <Badge bg="success" className="py-2 px-3">
                      <i className="fas fa-check-circle me-1"></i>
                      {selectedDate.toLocaleDateString("es-CO", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </Badge>
                  </div>
                )}
              </div>
            )}
          </Col>

          <Col md={5}>
            <div className="mb-3">
              <Form.Label className="fw-bold">
                <i className="fas fa-comment me-2"></i>
                Descripción (opcional)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Motivo de la reserva..."
                maxLength={500}
              />
              <small className="text-muted">{descripcion.length}/500</small>
            </div>

            {zona.reglas_uso && (
              <div className="mb-3">
                <Form.Label className="fw-bold">
                  <i className="fas fa-info-circle me-2"></i>
                  Reglas de uso
                </Form.Label>
                <div
                  className="p-3 bg-light rounded"
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                  dangerouslySetInnerHTML={{ __html: zona.reglas_uso }}
                />
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <button
          type="button"
          className="btn btn-light btn-xs"
          onClick={onHide}
          disabled={loading}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn-primary btn-xs"
          onClick={handleSubmit}
          disabled={loading || limiteAlcanzado}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Reservando...
            </>
          ) : (
            <>
              <i className="fas fa-calendar-check me-2"></i>
              Confirmar Reserva
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReservaModal;
